import moment from 'moment';
import store from '../../state';
import { createActionCable } from '../../utils/actioncable';
import * as availabilityRequirementSchedulesOperations from '../../state/modules/availability-requirement-schedules/operations';
import * as combinedScheduleOperations from '../../state/modules/combined-schedules/operations';
import * as dashboardOperations from '../../state/modules/dashboards/operations';
import {
  selectDashboardDatetime,
  selectIsDashboardMode,
  selectCurrentDashboard
} from '../../state/modules/dashboards/selectors';
import { registerForIncidentUpdates as registerForIncidentUpdatesInternal } from '../../state/modules/incidents/operations';
import { getAccessScopeGroupIds } from './utils';

let instance;

const MIN_SUBSCRIPTION_TIME = 900_000; // Miliseconds = 15 minutes

// WARNING: Do not call this directly, but use DashboardAPIManager instead
export default class DashboardRegistrationManager {
  constructor() {
    if (instance) {
      return instance;
    }

    store.subscribe(this.handleChanged);

    this.dashboardDatetime = this.getDashboardDatetime();
    this.lastSubscriptionResetAt = null;
    this.locked = false;
    this.subscriptions = {};
    this.batchedUpdates = [];
    instance = this;
  }

  getDashboardDatetime = () => selectDashboardDatetime(store.getState()).dateTime || moment().format();

  handleChanged = () => {
    const { dateTime, locked } = selectDashboardDatetime(store.getState());

    if (!moment(dateTime).isSame(this.dashboardDatetime) || locked !== this.locked) {
      this.setDashboardDateTime(dateTime, locked);
    }
  };

  waitForConnection = () =>
    new Promise(resolve => {
      const checkConnection = () => {
        if (this.actionCable) {
          return resolve(this.actionCable);
        } else if (!selectIsDashboardMode(store.getState())) {
          this.actionCable = createActionCable();
          return resolve(this.actionCable);
        }

        // If an auth token is available to register for regional information,
        // use it. To do so, create a separate actioncable connection which
        // passes in the access token. This will create a new connection with
        // the credentials of a region user, instead of using the connection
        // registered to the current user.
        const token = store.getState().dashboards.dashboardReadAuthToken;
        const currentDashboard = selectCurrentDashboard(store.getState());
        const accessScopeGroupIds = currentDashboard?.access_scope_group_ids || [];
        // Enforce that the token must have a value if:
        // 1. There are more than 1 access scope group IDs, or
        // 2. There is exactly 1 access scope group ID, and it is different from the current dashboard's group ID
        const enforceToken =
          accessScopeGroupIds.length > 1 ||
          (accessScopeGroupIds.length === 1 && currentDashboard?.group_id !== accessScopeGroupIds[0]);
        if (token !== undefined && !this.actionCable && (!enforceToken || token !== null)) {
          this.actionCable = createActionCable(token);
          resolve(this.actionCable);
        } else {
          setTimeout(checkConnection, 100);
        }
      };
      checkConnection();
    });

  registerForAvailabilityRequirementScheduleUpdates = params =>
    this.subscribe('AvailabilityRequirementNotificationsChannel', params);

  registerForCombinedScheduleUpdates = params => this.subscribe('CombinedSchedulesNotificationsChannel', params);

  registerForIncidentUpdates = async ({ groupId } = {}) => {
    const actionCable = await this.waitForConnection();

    const accessScopeGroupIds = ((await getAccessScopeGroupIds()) || [groupId]).filter(Boolean);

    // if we have an access_scope_group_id, register for all incident updates
    // within that scope. Only templates don't have these, and no subscription
    // needs to take place
    if (accessScopeGroupIds.length > 0) {
      console.log('Registering for incident updates for Group IDs', accessScopeGroupIds);
      store.dispatch(registerForIncidentUpdatesInternal(accessScopeGroupIds, actionCable));
    } else {
      console.log('No access_scope_group_id. Assuming dashboard template. Not registering to incident updates');
    }
  };

  registerForDashboardUpdates = params => this.subscribe('DashboardNotificationChannel', params);

  async subscribe(channel, params) {
    // Maps channels to operations that should be called to handle the actual
    // subscription
    const operations = {
      CombinedSchedulesNotificationsChannel: combinedScheduleOperations.registerForCombinedScheduleUpdates,
      AvailabilityRequirementNotificationsChannel:
        availabilityRequirementSchedulesOperations.registerForAvailabilityRequirementScheduleUpdates,
      DashboardNotificationChannel: dashboardOperations.registerForDashboardUpdates
    };

    const actionCable = await this.waitForConnection();
    const operation = operations[channel];
    if (!operation) throw new Error(`Operation not found for channel ${channel}`);

    params.groupIds = (await getAccessScopeGroupIds()) || [params.groupIds, params.groupId].flat().filter(Boolean);

    if (this.locked) {
      params.start_time = this.dashboardDatetime.format();
    }
    const keyParams = { channel, ...params };
    const key = JSON.stringify(keyParams);
    if (this.subscriptions[key]) return;
    this.subscriptions[key] = {
      subscription: store.dispatch(operation(params, actionCable)),
      params
    };
  }

  removeSubscription(key) {
    this.subscriptions[key]?.subscription?.unsubscribe();
    delete this.subscriptions[key];
  }

  // Should be triggered by the dashboard when the dashboardDatetime changes
  setDashboardDateTime(dashboardDatetime, locked) {
    // Remove any lock to subscription reset
    this.lastSubscriptionResetAt = null;
    this.dashboardDatetime = moment(dashboardDatetime);
    if (!this.locked && !locked) return;
    this.locked = locked;
    this.resetSubscriptions();
  }

  canReset() {
    if (!this.lastSubscriptionResetAt) return true;

    return moment().diff(this.lastSubscriptionResetAt) >= MIN_SUBSCRIPTION_TIME;
  }

  // Resets subscriptions with the new dashboardDatetime
  resetSubscriptions() {
    if (!this.canReset()) return;

    let newSubscriptions = [];
    for (let key in this.subscriptions) {
      const params = this.subscriptions[key].params;
      const channel = JSON.parse(key).channel;
      newSubscriptions.push({ params, channel });
      this.removeSubscription(key);
    }
    for (const { params, channel } of newSubscriptions) {
      this.subscribe(channel, params);
    }

    this.lastSubscriptionResetAt = moment();
  }
}
